import supabaseClient from "@/api/supabaseClient";
import { FunctionsHttpError, SupabaseClient } from "@supabase/supabase-js";
import { useMutation } from "@tanstack/react-query";
import { BackendErrorResponse } from "@/types/response/BackendErrorResponse";
import { BackendSuccessResponse } from "@/types/response/BackendSuccessResponse";
import { GenerateItineraryRequest } from "$/requests/GenerateItineraryRequest.ts";
import { queryClient } from "@/main.tsx";

export class GenerateItineraryRequestError extends Error {
  constructor(
    message: string,
    readonly status?: number,
  ) {
    super(message);
    this.name = "GenerateItineraryRequestError";
  }
}

const useGenerateItinerary = () => {
  const client = supabaseClient;

  const mutationFn = async (request: GenerateItineraryRequest) => {
    return generateItinerary(client, request);
  };

  return useMutation({
    mutationFn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["itineraries"] });
      void queryClient.invalidateQueries({ queryKey: ["user"] });
    },
  });
};

export default useGenerateItinerary;

async function generateItinerary(
  client: SupabaseClient,
  request: GenerateItineraryRequest,
): Promise<BackendSuccessResponse<{ queued: boolean; kind?: string }>> {
  const { data, error } = await client.functions.invoke("generate_itinerary", {
    body: { ...request },
  });

  if (error && error instanceof FunctionsHttpError) {
    const status: number | undefined = error.context.status;
    const errorResponse = (await error.context.json()) as
      & Partial<BackendErrorResponse>
      & {
        message?: string;
        error?: string;
      };

    const errorMessage = Array.isArray(errorResponse.errors)
      ? errorResponse.errors.join(", ")
      : errorResponse.message ??
        errorResponse.error ??
        "Failed to generate itinerary";

    throw new GenerateItineraryRequestError(errorMessage, status);
  }

  if (error) {
    throw new GenerateItineraryRequestError(error.message);
  }

  if (!data?.data) {
    throw new GenerateItineraryRequestError("Itinerary generation request returned no data");
  }

  return data;
}
